import clsx from "clsx";
import React, { useRef } from "react";
import { Popover } from "radix-ui";

import {
  CLASSES,
  KEYS,
  capitalizeString,
  isTransparent,
} from "@excalidraw/common";

import {
  shouldAllowVerticalAlign,
  suppportsHorizontalAlign,
  hasBoundTextElement,
  isElbowArrow,
  isImageElement,
  isLinearElement,
  isTextElement,
  isArrowElement,
  hasStrokeColor,
  toolIsArrow,
} from "@excalidraw/element";

import type {
  ExcalidrawElement,
  ExcalidrawElementType,
  NonDeletedElementsMap,
  NonDeletedSceneElementsMap,
} from "@excalidraw/element/types";

import { actionToggleZenMode } from "../actions";

import { alignActionsPredicate } from "../actions/actionAlign";
import { trackEvent } from "../analytics";

import { t } from "../i18n";
import {
  canChangeRoundness,
  canHaveArrowheads,
  getTargetElements,
  hasBackground,
  hasStrokeStyle,
  hasStrokeWidth,
} from "../scene";

import { getFormValue } from "../actions/actionProperties";

import { useTextEditorFocus } from "../hooks/useTextEditorFocus";

import { actionToggleViewMode } from "../actions/actionToggleViewMode";

import { getToolbarTools } from "./shapes";

import "./Actions.scss";

import {
  useEditorInterface,
  useStylesPanelMode,
  useExcalidrawContainer,
  useExplorerPropertiesPortal,
} from "./App";
import Stack from "./Stack";
import { ToolButton } from "./ToolButton";
import { ToolPopover } from "./ToolPopover";
import { Tooltip } from "./Tooltip";
import { PropertiesPopover } from "./PropertiesPopover";
import {
  frameToolIcon,
  laserPointerToolIcon,
  LassoIcon,
  sharpArrowIcon,
  roundArrowIcon,
  elbowArrowIcon,
  TextSizeIcon,
  adjustmentsIcon,
  DotsHorizontalIcon,
  SelectionIcon,
  pencilIcon,
  papelIcon,
  RectangleIcon,
  resizeIcon,
  TriangleIcon,
  EllipseIcon,
  ArrowIcon,
  LineIcon,
  FreedrawIcon,
  GeometricShapesIcon,
} from "./icons";

import { Island } from "./Island";

import type {
  AppClassProperties,
  AppProps,
  UIAppState,
  Zoom,
  AppState,
} from "../types";
import type { ActionManager } from "../actions/manager";

// Common CSS class combinations
const PROPERTIES_CLASSES = clsx([
  CLASSES.SHAPE_ACTIONS_THEME_SCOPE,
  "properties-content",
]);

export const canChangePapelFormat = (
  appState: UIAppState,
  targetElements: ExcalidrawElement[],
) => {
  return (
    appState.activeTool.customType === "papel" ||
    (targetElements.length > 0 &&
      targetElements.every((el) => el.customData?.isPapel))
  );
};

export const canChangeStrokeColor = (
  appState: UIAppState,
  targetElements: ExcalidrawElement[],
) => {
  let commonSelectedType: ExcalidrawElementType | null =
    targetElements[0]?.type || null;

  for (const element of targetElements) {
    if (element.type !== commonSelectedType) {
      commonSelectedType = null;
      break;
    }
  }

  return (
    (hasStrokeColor(appState.activeTool.type) &&
      commonSelectedType !== "image" &&
      commonSelectedType !== "frame" &&
      commonSelectedType !== "magicframe") ||
    targetElements.some((element) => hasStrokeColor(element.type))
  );
};

export const canChangeBackgroundColor = (
  appState: UIAppState,
  targetElements: ExcalidrawElement[],
) => {
  return (
    hasBackground(appState.activeTool.type) ||
    targetElements.some((element) => hasBackground(element.type))
  );
};

export const SelectedShapeActions = ({
  appState,
  elementsMap,
  renderAction,
  app,
}: {
  appState: UIAppState;
  elementsMap: NonDeletedElementsMap | NonDeletedSceneElementsMap;
  renderAction: ActionManager["renderAction"];
  app: AppClassProperties;
}) => {
  const targetElements = getTargetElements(elementsMap, appState);

  let isSingleElementBoundContainer = false;
  if (
    targetElements.length === 2 &&
    (hasBoundTextElement(targetElements[0]) ||
      hasBoundTextElement(targetElements[1]))
  ) {
    isSingleElementBoundContainer = true;
  }
  const isEditingTextOrNewElement = Boolean(
    appState.editingTextElement || appState.newElement,
  );
  const editorInterface = useEditorInterface();
  const isRTL = document.documentElement.getAttribute("dir") === "rtl";

  const showFillIcons =
    (hasBackground(appState.activeTool.type) &&
      !isTransparent(appState.currentItemBackgroundColor)) ||
    targetElements.some(
      (element) =>
        hasBackground(element.type) && !isTransparent(element.backgroundColor),
    );

  const showLinkIcon =
    targetElements.length === 1 || isSingleElementBoundContainer;

  const showLineEditorAction =
    !appState.selectedLinearElement?.isEditing &&
    targetElements.length === 1 &&
    isLinearElement(targetElements[0]) &&
    !isElbowArrow(targetElements[0]);

  const showCropEditorAction =
    !appState.croppingElementId &&
    targetElements.length === 1 &&
    isImageElement(targetElements[0]);

  const showAlignActions =
    !isSingleElementBoundContainer && alignActionsPredicate(appState, app);

  return (
    <div className="selected-shape-actions">
      {canChangePapelFormat(appState, targetElements) && (
        <div>{renderAction("changePapelFormat")}</div>
      )}
      <div>
        {canChangeStrokeColor(appState, targetElements) &&
          renderAction("changeStrokeColor")}
      </div>
      {canChangeBackgroundColor(appState, targetElements) && (
        <div>{renderAction("changeBackgroundColor")}</div>
      )}
      {showFillIcons && renderAction("changeFillStyle")}

      {(hasStrokeWidth(appState.activeTool.type) ||
        targetElements.some((element) => hasStrokeWidth(element.type))) &&
        renderAction("changeStrokeWidth")}

      {(appState.activeTool.type === "freedraw" ||
        targetElements.some((element) => element.type === "freedraw")) &&
        renderAction("changeStrokeShape")}

      {(hasStrokeStyle(appState.activeTool.type) ||
        targetElements.some((element) => hasStrokeStyle(element.type))) && (
        <>
          {renderAction("changeStrokeStyle")}
          {renderAction("changeSloppiness")}
        </>
      )}

      {(canChangeRoundness(appState.activeTool.type) ||
        targetElements.some((element) => canChangeRoundness(element.type))) && (
        <>{renderAction("changeRoundness")}</>
      )}

      {(toolIsArrow(appState.activeTool.type) ||
        targetElements.some((element) => toolIsArrow(element.type))) && (
        <>{renderAction("changeArrowType")}</>
      )}

      {(appState.activeTool.type === "text" ||
        targetElements.some(isTextElement)) && (
        <>
          <fieldset>{renderAction("changeFontFamily")}</fieldset>
          {renderAction("changeFontSize")}
          {(appState.activeTool.type === "text" ||
            suppportsHorizontalAlign(targetElements, elementsMap)) &&
            renderAction("changeTextAlign")}
        </>
      )}

      {shouldAllowVerticalAlign(targetElements, elementsMap) &&
        renderAction("changeVerticalAlign")}
      {(canHaveArrowheads(appState.activeTool.type) ||
        targetElements.some((element) => canHaveArrowheads(element.type))) && (
        <>{renderAction("changeArrowhead")}</>
      )}

      {renderAction("changeOpacity")}

      {([
        "arrow",
        "line",
        "rectangle",
        "triangle",
        "diamond",
        "ellipse",
        "text",
        "freedraw",
        "image",
        "iframe",
        "embeddable",
        "frame",
        "magicframe",
      ].includes(appState.activeTool.type) ||
        targetElements.some((element) =>
          [
            "arrow",
            "line",
            "rectangle",
            "triangle",
            "diamond",
            "ellipse",
            "text",
            "freedraw",
            "image",
            "iframe",
            "embeddable",
            "frame",
            "magicframe",
          ].includes(element.type),
        )) && (
        <fieldset style={{ marginTop: "1rem" }}>
          {renderAction("changeDepth")}
        </fieldset>
      )}

      <fieldset>
        <legend>{t("labels.layers")}</legend>
        <div className="buttonList">
          {renderAction("sendToBack")}
          {renderAction("sendBackward")}
          {renderAction("bringForward")}
          {renderAction("bringToFront")}
        </div>
      </fieldset>

      {targetElements.length > 0 &&
        targetElements.every(
          (el) => el.type !== "line" && el.type !== "arrow",
        ) &&
        renderAction("rotatePapel")}

      {showAlignActions && !isSingleElementBoundContainer && (
        <fieldset>
          <legend>{t("labels.align")}</legend>
          <div className="buttonList">
            {
              // swap this order for RTL so the button positions always match their action
              // (i.e. the leftmost button aligns left)
            }
            {isRTL ? (
              <>
                {renderAction("alignRight")}
                {renderAction("alignHorizontallyCentered")}
                {renderAction("alignLeft")}
              </>
            ) : (
              <>
                {renderAction("alignLeft")}
                {renderAction("alignHorizontallyCentered")}
                {renderAction("alignRight")}
              </>
            )}
            {targetElements.length > 2 &&
              renderAction("distributeHorizontally")}
            {/* breaks the row ˇˇ */}
            <div style={{ flexBasis: "100%", height: 0 }} />
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: ".5rem",
                marginTop: "-0.5rem",
              }}
            >
              {renderAction("alignTop")}
              {renderAction("alignVerticallyCentered")}
              {renderAction("alignBottom")}
              {targetElements.length > 2 &&
                renderAction("distributeVertically")}
            </div>
          </div>
        </fieldset>
      )}
      {!isEditingTextOrNewElement && targetElements.length > 0 && (
        <fieldset>
          <legend>{t("labels.actions")}</legend>
          <div className="buttonList">
            {editorInterface.formFactor !== "phone" &&
              renderAction("duplicateSelection")}
            {editorInterface.formFactor !== "phone" &&
              renderAction("deleteSelectedElements")}
            {renderAction("group")}
            {renderAction("ungroup")}
            {showLinkIcon && renderAction("hyperlink")}
            {showCropEditorAction && renderAction("cropEditor")}
            {showLineEditorAction && renderAction("toggleLinearEditor")}
          </div>
        </fieldset>
      )}
    </div>
  );
};

const CombinedShapeProperties = ({
  appState,
  renderAction,
  setAppState,
  targetElements,
  container,
}: {
  targetElements: ExcalidrawElement[];
  appState: UIAppState;
  renderAction: ActionManager["renderAction"];
  setAppState: React.Component<any, AppState>["setState"];
  container: HTMLDivElement | null;
}) => {
  const explorerPropertiesPortal = useExplorerPropertiesPortal();
  const popoverContainer = explorerPropertiesPortal?.container ?? container;
  const popoverOpenPopup =
    explorerPropertiesPortal?.openPopup ?? appState.openPopup;
  const setPopupOpen = (openPopup: AppState["openPopup"]) => {
    if (explorerPropertiesPortal) {
      explorerPropertiesPortal.setOpenPopup(openPopup);
      setAppState({ openPopup: null });
      return;
    }

    setAppState({ openPopup });
  };
  const clearPopupOpen = () => {
    if (explorerPropertiesPortal) {
      explorerPropertiesPortal.setOpenPopup(null);
      return;
    }

    setAppState({ openPopup: null });
  };
  const showFillIcons =
    (hasBackground(appState.activeTool.type) &&
      !isTransparent(appState.currentItemBackgroundColor)) ||
    targetElements.some(
      (element) =>
        hasBackground(element.type) && !isTransparent(element.backgroundColor),
    );

  const shouldShowCombinedProperties =
    targetElements.length > 0 ||
    (appState.activeTool.type !== "selection" &&
      appState.activeTool.type !== "eraser" &&
      appState.activeTool.type !== "hand" &&
      appState.activeTool.type !== "laser" &&
      appState.activeTool.type !== "lasso");
  const isOpen = popoverOpenPopup === "compactStrokeStyles";

  if (!shouldShowCombinedProperties) {
    return null;
  }

  return (
    <div className="compact-action-item">
      <Popover.Root
        open={isOpen}
        onOpenChange={(open) => {
          if (open) {
            setPopupOpen("compactStrokeStyles");
            return;
          }

          clearPopupOpen();
        }}
      >
        <Popover.Trigger asChild>
          <button
            type="button"
            className={clsx("compact-action-button properties-trigger", {
              active: isOpen,
            })}
            title={t("labels.stroke")}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              if (isOpen) {
                clearPopupOpen();
                return;
              }

              setPopupOpen("compactStrokeStyles");
            }}
          >
            {adjustmentsIcon}
          </button>
        </Popover.Trigger>
        {isOpen && (
          <PropertiesPopover
            className={PROPERTIES_CLASSES}
            container={popoverContainer}
            style={{ maxWidth: "13rem" }}
            onClose={clearPopupOpen}
          >
            <div className="selected-shape-actions">
              {[
                showFillIcons ? renderAction("changeFillStyle") : null,
                hasStrokeWidth(appState.activeTool.type) ||
                targetElements.some((element) => hasStrokeWidth(element.type))
                  ? renderAction("changeStrokeWidth")
                  : null,
                appState.activeTool.type === "freedraw" ||
                targetElements.some((element) => element.type === "freedraw")
                  ? renderAction("changeStrokeShape")
                  : null,
                hasStrokeStyle(appState.activeTool.type) ||
                targetElements.some((element) => hasStrokeStyle(element.type))
                  ? renderAction("changeStrokeStyle")
                  : null,
                hasStrokeStyle(appState.activeTool.type) ||
                targetElements.some((element) => hasStrokeStyle(element.type))
                  ? renderAction("changeSloppiness")
                  : null,
                canChangeRoundness(appState.activeTool.type) ||
                targetElements.some((element) =>
                  canChangeRoundness(element.type),
                )
                  ? renderAction("changeRoundness")
                  : null,
                renderAction("changeOpacity"),
                [
                  "arrow",
                  "line",
                  "rectangle",
                  "triangle",
                  "diamond",
                  "ellipse",
                  "text",
                  "freedraw",
                  "image",
                  "iframe",
                  "embeddable",
                  "frame",
                  "magicframe",
                ].includes(appState.activeTool.type) ||
                targetElements.some((element) =>
                  [
                    "arrow",
                    "line",
                    "rectangle",
                    "triangle",
                    "diamond",
                    "ellipse",
                    "text",
                    "freedraw",
                    "image",
                    "iframe",
                    "embeddable",
                    "frame",
                    "magicframe",
                  ].includes(element.type),
                )
                  ? renderAction("changeDepth")
                  : null,
              ]
                .filter(Boolean)
                .map((item, index, arr) => (
                  <React.Fragment key={index}>
                    {item}
                    {index < arr.length - 1 && (
                      <div
                        style={{
                          height: "1px",
                          background:
                            "linear-gradient(90deg, transparent 0%, var(--onepaper-explorer-separator, var(--default-border-color)) 50%, transparent 100%)",
                          opacity: 0.6,
                          margin: "0 0.5rem",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </React.Fragment>
                ))}
            </div>
          </PropertiesPopover>
        )}
      </Popover.Root>
    </div>
  );
};

const CombinedArrowProperties = ({
  appState,
  renderAction,
  setAppState,
  targetElements,
  container,
  app,
}: {
  targetElements: ExcalidrawElement[];
  appState: UIAppState;
  renderAction: ActionManager["renderAction"];
  setAppState: React.Component<any, AppState>["setState"];
  container: HTMLDivElement | null;
  app: AppClassProperties;
}) => {
  const explorerPropertiesPortal = useExplorerPropertiesPortal();
  const popoverContainer = explorerPropertiesPortal?.container ?? container;
  const popoverOpenPopup =
    explorerPropertiesPortal?.openPopup ?? appState.openPopup;
  const setPopupOpen = (openPopup: AppState["openPopup"]) => {
    if (explorerPropertiesPortal) {
      explorerPropertiesPortal.setOpenPopup(openPopup);
      setAppState({ openPopup: null });
      return;
    }

    setAppState({ openPopup });
  };
  const clearPopupOpen = () => {
    if (explorerPropertiesPortal) {
      explorerPropertiesPortal.setOpenPopup(null);
      return;
    }

    setAppState({ openPopup: null });
  };
  const showShowArrowProperties =
    toolIsArrow(appState.activeTool.type) ||
    targetElements.some((element) => toolIsArrow(element.type));
  const isOpen = popoverOpenPopup === "compactArrowProperties";

  if (!showShowArrowProperties) {
    return null;
  }

  return (
    <div className="compact-action-item">
      <Popover.Root
        open={isOpen}
        onOpenChange={(open) => {
          if (open) {
            setPopupOpen("compactArrowProperties");
            return;
          }

          clearPopupOpen();
        }}
      >
        <Popover.Trigger asChild>
          <button
            type="button"
            className={clsx("compact-action-button properties-trigger", {
              active: isOpen,
            })}
            title={t("labels.arrowtypes")}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              if (isOpen) {
                clearPopupOpen();
                return;
              }

              setPopupOpen("compactArrowProperties");
            }}
          >
            {(() => {
              // Show an icon based on the current arrow type
              const arrowType = getFormValue(
                targetElements,
                app,
                (element) => {
                  if (isArrowElement(element)) {
                    return element.elbowed
                      ? "elbow"
                      : element.roundness
                      ? "round"
                      : "sharp";
                  }
                  return null;
                },
                (element) => isArrowElement(element),
                (hasSelection) =>
                  hasSelection ? null : appState.currentItemArrowType,
              );

              if (arrowType === "elbow") {
                return elbowArrowIcon;
              }
              if (arrowType === "round") {
                return roundArrowIcon;
              }
              return sharpArrowIcon;
            })()}
          </button>
        </Popover.Trigger>
        {isOpen && (
          <PropertiesPopover
            container={popoverContainer}
            className="properties-content"
            style={{ maxWidth: "13rem" }}
            onClose={clearPopupOpen}
          >
            {renderAction("changeArrowProperties")}
          </PropertiesPopover>
        )}
      </Popover.Root>
    </div>
  );
};

const CombinedTextProperties = ({
  appState,
  renderAction,
  setAppState,
  targetElements,
  container,
  elementsMap,
}: {
  appState: UIAppState;
  renderAction: ActionManager["renderAction"];
  setAppState: React.Component<any, AppState>["setState"];
  targetElements: ExcalidrawElement[];
  container: HTMLDivElement | null;
  elementsMap: NonDeletedElementsMap | NonDeletedSceneElementsMap;
}) => {
  const { saveCaretPosition, restoreCaretPosition } = useTextEditorFocus();
  const explorerPropertiesPortal = useExplorerPropertiesPortal();
  const popoverContainer = explorerPropertiesPortal?.container ?? container;
  const popoverOpenPopup =
    explorerPropertiesPortal?.openPopup ?? appState.openPopup;
  const isOpen = popoverOpenPopup === "compactTextProperties";
  const setPopupOpen = (openPopup: AppState["openPopup"]) => {
    if (explorerPropertiesPortal) {
      explorerPropertiesPortal.setOpenPopup(openPopup);
      setAppState({ openPopup: null });
      return;
    }

    setAppState({ openPopup });
  };
  const clearPopupOpen = () => {
    if (explorerPropertiesPortal) {
      explorerPropertiesPortal.setOpenPopup(null);
      return;
    }

    setAppState({ openPopup: null });
  };

  return (
    <div className="compact-action-item">
      <Popover.Root
        open={isOpen}
        onOpenChange={(open) => {
          if (open) {
            if (appState.editingTextElement) {
              saveCaretPosition();
            }
            setPopupOpen("compactTextProperties");
            return;
          }

          clearPopupOpen();
          if (appState.editingTextElement) {
            restoreCaretPosition();
          }
        }}
      >
        <Popover.Trigger asChild>
          <button
            type="button"
            className={clsx("compact-action-button properties-trigger", {
              active: isOpen,
            })}
            title={t("labels.textAlign")}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              if (isOpen) {
                clearPopupOpen();
                return;
              }

              if (appState.editingTextElement) {
                saveCaretPosition();
              }
              setPopupOpen("compactTextProperties");
            }}
          >
            {TextSizeIcon}
          </button>
        </Popover.Trigger>
        {isOpen && (
          <PropertiesPopover
            className={PROPERTIES_CLASSES}
            container={popoverContainer}
            style={{ maxWidth: "13rem" }}
            // Improve focus handling for text editing scenarios
            preventAutoFocusOnTouch={!!appState.editingTextElement}
            onClose={() => {
              // Refocus text editor when popover closes with caret restoration
              if (appState.editingTextElement) {
                restoreCaretPosition();
              }
              clearPopupOpen();
            }}
          >
            <div className="selected-shape-actions">
              {[
                appState.activeTool.type === "text" ||
                targetElements.some(isTextElement)
                  ? renderAction("changeFontSize")
                  : null,
                appState.activeTool.type === "text" ||
                suppportsHorizontalAlign(targetElements, elementsMap)
                  ? renderAction("changeTextAlign")
                  : null,
                shouldAllowVerticalAlign(targetElements, elementsMap)
                  ? renderAction("changeVerticalAlign")
                  : null,
              ]
                .filter(Boolean)
                .map((item, index, arr) => (
                  <React.Fragment key={index}>
                    {item}
                    {index < arr.length - 1 && (
                      <div
                        style={{
                          height: "1px",
                          background:
                            "linear-gradient(90deg, transparent 0%, var(--onepaper-explorer-separator, var(--default-border-color)) 50%, transparent 100%)",
                          opacity: 0.6,
                          margin: "0 0.5rem",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </React.Fragment>
                ))}
            </div>
          </PropertiesPopover>
        )}
      </Popover.Root>
    </div>
  );
};

const CombinedExtraActions = ({
  appState,
  renderAction,
  targetElements,
  setAppState,
  container,
  app,
  showDuplicate,
  showDelete,
}: {
  appState: UIAppState;
  targetElements: ExcalidrawElement[];
  renderAction: ActionManager["renderAction"];
  setAppState: React.Component<any, AppState>["setState"];
  container: HTMLDivElement | null;
  app: AppClassProperties;
  showDuplicate?: boolean;
  showDelete?: boolean;
}) => {
  const isEditingTextOrNewElement = Boolean(
    appState.editingTextElement || appState.newElement,
  );
  const showCropEditorAction =
    !appState.croppingElementId &&
    targetElements.length === 1 &&
    isImageElement(targetElements[0]);
  const showLinkIcon = targetElements.length === 1;
  const showAlignActions = alignActionsPredicate(appState, app);
  let isSingleElementBoundContainer = false;
  if (
    targetElements.length === 2 &&
    (hasBoundTextElement(targetElements[0]) ||
      hasBoundTextElement(targetElements[1]))
  ) {
    isSingleElementBoundContainer = true;
  }

  const isRTL = document.documentElement.getAttribute("dir") === "rtl";
  const isOpen = appState.openPopup === "compactOtherProperties";

  if (isEditingTextOrNewElement || targetElements.length === 0) {
    return null;
  }

  return (
    <div className="compact-action-item">
      <Popover.Root
        open={isOpen}
        onOpenChange={(open) => {
          if (open) {
            setAppState({ openPopup: "compactOtherProperties" });
          } else {
            setAppState({ openPopup: null });
          }
        }}
      >
        <Popover.Trigger asChild>
          <button
            type="button"
            className={clsx("compact-action-button properties-trigger", {
              active: isOpen,
            })}
            title={t("labels.actions")}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setAppState({
                openPopup: isOpen ? null : "compactOtherProperties",
              });
            }}
          >
            {DotsHorizontalIcon}
          </button>
        </Popover.Trigger>
        {isOpen && (
          <PropertiesPopover
            className={PROPERTIES_CLASSES}
            container={container}
            style={{
              maxWidth: "12rem",
              justifyContent: "center",
              alignItems: "center",
            }}
            onClose={() => {}}
          >
            <div className="selected-shape-actions">
              {[
                canChangePapelFormat(appState, targetElements) ? (
                  <React.Fragment key="format">
                    {renderAction("changePapelFormat")}
                  </React.Fragment>
                ) : null,
                <fieldset key="layers">
                  <legend>{t("labels.layers")}</legend>
                  <div className="buttonList">
                    {renderAction("sendToBack")}
                    {renderAction("sendBackward")}
                    {renderAction("bringForward")}
                    {renderAction("bringToFront")}
                  </div>
                </fieldset>,
                targetElements.length > 0 &&
                targetElements.every(
                  (el) => el.type !== "line" && el.type !== "arrow",
                ) ? (
                  <React.Fragment key="rotate">
                    {renderAction("rotatePapel")}
                  </React.Fragment>
                ) : null,
                showAlignActions && !isSingleElementBoundContainer ? (
                  <fieldset key="align">
                    <legend>{t("labels.align")}</legend>
                    <div className="buttonList">
                      {isRTL ? (
                        <>
                          {renderAction("alignRight")}
                          {renderAction("alignHorizontallyCentered")}
                          {renderAction("alignLeft")}
                        </>
                      ) : (
                        <>
                          {renderAction("alignLeft")}
                          {renderAction("alignHorizontallyCentered")}
                          {renderAction("alignRight")}
                        </>
                      )}
                      {targetElements.length > 2 &&
                        renderAction("distributeHorizontally")}
                      {/* breaks the row ˇˇ */}
                      <div style={{ flexBasis: "100%", height: 0 }} />
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: ".5rem",
                          marginTop: "-0.5rem",
                        }}
                      >
                        {renderAction("alignTop")}
                        {renderAction("alignVerticallyCentered")}
                        {renderAction("alignBottom")}
                        {targetElements.length > 2 &&
                          renderAction("distributeVertically")}
                      </div>
                    </div>
                  </fieldset>
                ) : null,
                <fieldset key="actions">
                  <legend>{t("labels.actions")}</legend>
                  <div className="buttonList">
                    {renderAction("group")}
                    {renderAction("ungroup")}
                    {showLinkIcon && renderAction("hyperlink")}
                    {showCropEditorAction && renderAction("cropEditor")}
                    {showDuplicate && renderAction("duplicateSelection")}
                    {showDelete && renderAction("deleteSelectedElements")}
                  </div>
                </fieldset>,
              ]
                .filter(Boolean)
                .map((item, index, arr) => (
                  <React.Fragment key={index}>
                    {item}
                    {index < arr.length - 1 && (
                      <div
                        style={{
                          height: "1px",
                          background:
                            "linear-gradient(90deg, transparent 0%, var(--onepaper-explorer-separator, var(--default-border-color)) 50%, transparent 100%)",
                          opacity: 0.6,
                          margin: "0 0.5rem",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </React.Fragment>
                ))}
            </div>
          </PropertiesPopover>
        )}
      </Popover.Root>
    </div>
  );
};

const LinearEditorAction = ({
  appState,
  renderAction,
  targetElements,
}: {
  appState: UIAppState;
  targetElements: ExcalidrawElement[];
  renderAction: ActionManager["renderAction"];
}) => {
  const showLineEditorAction =
    !appState.selectedLinearElement?.isEditing &&
    targetElements.length === 1 &&
    isLinearElement(targetElements[0]) &&
    !isElbowArrow(targetElements[0]);

  if (!showLineEditorAction) {
    return null;
  }

  return (
    <div className="compact-action-item">
      {renderAction("toggleLinearEditor")}
    </div>
  );
};

export const CompactShapeActions = ({
  appState,
  elementsMap,
  renderAction,
  app,
  setAppState,
}: {
  appState: UIAppState;
  elementsMap: NonDeletedElementsMap | NonDeletedSceneElementsMap;
  renderAction: ActionManager["renderAction"];
  app: AppClassProperties;
  setAppState: React.Component<any, AppState>["setState"];
}) => {
  const targetElements = getTargetElements(elementsMap, appState);
  const { container } = useExcalidrawContainer();

  const isEditingTextOrNewElement = Boolean(
    appState.editingTextElement || appState.newElement,
  );

  const showLineEditorAction =
    !appState.selectedLinearElement?.isEditing &&
    targetElements.length === 1 &&
    isLinearElement(targetElements[0]) &&
    !isElbowArrow(targetElements[0]);

  return (
    <div className="compact-shape-actions">
      {/* Stroke Color */}
      {canChangeStrokeColor(appState, targetElements) && (
        <div className={clsx("compact-action-item")}>
          {renderAction("changeStrokeColor")}
        </div>
      )}

      {/* Background Color */}
      {canChangeBackgroundColor(appState, targetElements) && (
        <div className="compact-action-item">
          {renderAction("changeBackgroundColor")}
        </div>
      )}

      <CombinedShapeProperties
        appState={appState}
        renderAction={renderAction}
        setAppState={setAppState}
        targetElements={targetElements}
        container={container}
      />

      <CombinedArrowProperties
        appState={appState}
        renderAction={renderAction}
        setAppState={setAppState}
        targetElements={targetElements}
        container={container}
        app={app}
      />
      {/* Linear Editor */}
      {showLineEditorAction && (
        <div className="compact-action-item">
          {renderAction("toggleLinearEditor")}
        </div>
      )}

      {/* Text Properties */}
      {(appState.activeTool.type === "text" ||
        targetElements.some(isTextElement)) && (
        <>
          <div className="compact-action-item">
            {renderAction("changeFontFamily")}
          </div>
          <CombinedTextProperties
            appState={appState}
            renderAction={renderAction}
            setAppState={setAppState}
            targetElements={targetElements}
            container={container}
            elementsMap={elementsMap}
          />
        </>
      )}

      {/* Dedicated Copy Button */}
      {!isEditingTextOrNewElement && targetElements.length > 0 && (
        <div className="compact-action-item">
          {renderAction("duplicateSelection")}
        </div>
      )}

      {/* Dedicated Delete Button */}
      {!isEditingTextOrNewElement && targetElements.length > 0 && (
        <div className="compact-action-item">
          {renderAction("deleteSelectedElements")}
        </div>
      )}

      <CombinedExtraActions
        appState={appState}
        renderAction={renderAction}
        targetElements={targetElements}
        setAppState={setAppState}
        container={container}
        app={app}
      />
    </div>
  );
};

export const MobileShapeActions = ({
  appState,
  elementsMap,
  renderAction,
  app,
  setAppState,
}: {
  appState: UIAppState;
  elementsMap: NonDeletedElementsMap | NonDeletedSceneElementsMap;
  renderAction: ActionManager["renderAction"];
  app: AppClassProperties;
  setAppState: React.Component<any, AppState>["setState"];
}) => {
  const targetElements = getTargetElements(elementsMap, appState);
  const { container } = useExcalidrawContainer();
  const mobileActionsRef = useRef<HTMLDivElement>(null);

  const ACTIONS_WIDTH =
    mobileActionsRef.current?.getBoundingClientRect()?.width ?? 0;

  // 7 actions + 2 for undo/redo
  const MIN_ACTIONS = 9;

  const GAP = 6;
  const WIDTH = 32;

  const MIN_WIDTH = MIN_ACTIONS * WIDTH + (MIN_ACTIONS - 1) * GAP;

  const ADDITIONAL_WIDTH = WIDTH + GAP;

  const showDeleteOutside = ACTIONS_WIDTH >= MIN_WIDTH + ADDITIONAL_WIDTH;
  const showDuplicateOutside =
    ACTIONS_WIDTH >= MIN_WIDTH + 2 * ADDITIONAL_WIDTH;

  return (
    <Island
      className="compact-shape-actions mobile-shape-actions"
      style={{
        flexDirection: "row",
        boxShadow: "none",
        padding: 0,
        zIndex: 2,
        backgroundColor: "transparent",
        height: WIDTH * 1.35,
        marginBottom: 4,
        alignItems: "center",
        gap: GAP,
        pointerEvents: "none",
      }}
      ref={mobileActionsRef}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: GAP,
          flex: 1,
        }}
      >
        {canChangeStrokeColor(appState, targetElements) && (
          <div className={clsx("compact-action-item")}>
            {renderAction("changeStrokeColor")}
          </div>
        )}
        {canChangeBackgroundColor(appState, targetElements) && (
          <div className="compact-action-item">
            {renderAction("changeBackgroundColor")}
          </div>
        )}
        <CombinedShapeProperties
          appState={appState}
          renderAction={renderAction}
          setAppState={setAppState}
          targetElements={targetElements}
          container={container}
        />
        {/* Combined Arrow Properties */}
        <CombinedArrowProperties
          appState={appState}
          renderAction={renderAction}
          setAppState={setAppState}
          targetElements={targetElements}
          container={container}
          app={app}
        />
        {/* Linear Editor */}
        <LinearEditorAction
          appState={appState}
          renderAction={renderAction}
          targetElements={targetElements}
        />
        {/* Text Properties */}
        {(appState.activeTool.type === "text" ||
          targetElements.some(isTextElement)) && (
          <>
            <div className="compact-action-item">
              {renderAction("changeFontFamily")}
            </div>
            <CombinedTextProperties
              appState={appState}
              renderAction={renderAction}
              setAppState={setAppState}
              targetElements={targetElements}
              container={container}
              elementsMap={elementsMap}
            />
          </>
        )}

        {/* Combined Other Actions */}
        <CombinedExtraActions
          appState={appState}
          renderAction={renderAction}
          targetElements={targetElements}
          setAppState={setAppState}
          container={container}
          app={app}
          showDuplicate={!showDuplicateOutside}
          showDelete={!showDeleteOutside}
        />
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: GAP,
        }}
      >
        <div className="compact-action-item">{renderAction("undo")}</div>
        <div className="compact-action-item">{renderAction("redo")}</div>
        {showDuplicateOutside && (
          <div className="compact-action-item">
            {renderAction("duplicateSelection")}
          </div>
        )}
        {showDeleteOutside && (
          <div className="compact-action-item">
            {renderAction("deleteSelectedElements")}
          </div>
        )}
      </div>
    </Island>
  );
};

export const ShapesSwitcher = ({
  activeTool,
  setAppState,
  app,
  UIOptions,
  extraToolsMenuSide,
  radioGroupName = "editor-current-shape",
}: {
  activeTool: UIAppState["activeTool"];
  setAppState: React.Component<any, AppState>["setState"];
  app: AppClassProperties;
  UIOptions: AppProps["UIOptions"];
  extraToolsMenuSide?: "bottom" | "right";
  radioGroupName?: string;
}) => {
  const stylesPanelMode = useStylesPanelMode();
  const isCompactStylesPanel = stylesPanelMode === "compact";

  const SELECTION_TOOLS = [
    {
      type: "selection",
      icon: SelectionIcon,
      title: capitalizeString(t("toolBar.selection")),
    },
    {
      type: "lasso",
      icon: LassoIcon,
      title: capitalizeString(t("toolBar.lasso")),
    },
  ] as const;

  const GEOMETRIC_SHAPES = [
    {
      type: "rectangle",
      icon: RectangleIcon,
      title: `${capitalizeString(t("toolBar.rectangle"))} — ${KEYS["2"]}`,
    },
    {
      type: "triangle",
      icon: TriangleIcon,
      title: `${capitalizeString(t("toolBar.triangle"))} — ${KEYS["3"]}`,
    },
    {
      type: "ellipse",
      icon: EllipseIcon,
      title: `${capitalizeString(t("toolBar.ellipse"))} — ${KEYS["4"]}`,
    },
  ] as const;

  const FREEHAND_TOOLS = [
    {
      type: "freedraw",
      icon: FreedrawIcon,
      title: `${capitalizeString(t("toolBar.freedraw"))} — P ${t(
        "helpDialog.or",
      )} ${KEYS["7"]}`,
    },
    {
      type: "laser",
      icon: laserPointerToolIcon,
      title: `${capitalizeString(t("toolBar.laser"))} — ${KEYS.K}`,
    },
  ] as const;

  const CANVAS_TOOLS = [
    {
      type: "custom",
      customType: "papel",
      icon: papelIcon,
      title: "Papel",
      onChange: (appInstance: AppClassProperties) => {
        appInstance.setActiveTool({ type: "custom", customType: "papel" });
      },
    },
    {
      type: "frame",
      customType: null,
      icon: frameToolIcon,
      title: `${capitalizeString(t("toolBar.frame"))} — ${KEYS.F}`,
    },
  ] as const;

  const LINE_TOOLS = [
    {
      type: "arrow",
      icon: ArrowIcon,
      title: `${capitalizeString(t("toolBar.arrow"))} — ${KEYS["5"]}`,
    },
    {
      type: "line",
      icon: LineIcon,
      title: `${capitalizeString(t("toolBar.line"))} — ${KEYS["6"]}`,
    },
  ] as const;

  return (
    <>
      {getToolbarTools(app).map(
        ({ value, icon, key, numericKey, fillable, toolbar }) => {
          if (
            toolbar === false ||
            UIOptions.tools?.[
              value as Extract<
                typeof value,
                keyof AppProps["UIOptions"]["tools"]
              >
            ] === false
          ) {
            return null;
          }

          const label = t(`toolBar.${value}`);
          const letter =
            key && capitalizeString(typeof key === "string" ? key : key[0]);
          const shortcut = letter
            ? `${letter} ${t("helpDialog.or")} ${numericKey}`
            : `${numericKey}`;
          const keybindingLabel =
            value === "hand" ? undefined : numericKey || letter;

          const displayedCanvasTool =
            CANVAS_TOOLS.find(
              (tool) =>
                (tool.customType || tool.type) ===
                app.state.preferredCanvasTool.type,
            ) || CANVAS_TOOLS[0];

          const canvasPopoverJSX = (
            <ToolPopover
              key={"canvas-tools-popover"}
              app={app}
              options={CANVAS_TOOLS}
              activeTool={activeTool}
              defaultOption={app.state.preferredCanvasTool.type}
              namePrefix="canvasToolType"
              title={
                activeTool.type === "frame" ||
                (activeTool.type === "custom" &&
                  activeTool.customType === "papel")
                  ? displayedCanvasTool.title
                  : "Papel e Quadro"
              }
              data-testid="toolbar-canvas-tools"
              onToolChange={(type: string) => {
                if (type === "frame" || type === "papel") {
                  if (type === "frame") {
                    app.setActiveTool({ type });
                  }
                  setAppState({
                    preferredCanvasTool: {
                      type: type as "frame" | "papel",
                      initialized: true,
                    },
                  });
                }
              }}
              displayedOption={{
                ...displayedCanvasTool,
                icon:
                  activeTool.type === "frame" ||
                  (activeTool.type === "custom" &&
                    activeTool.customType === "papel")
                    ? displayedCanvasTool.icon
                    : papelIcon,
              }}
              fillable={false}
              selectOnOpen={false}
              side="right"
              align="center"
            />
          );

          // when in compact styles panel mode (tablet)
          // use a ToolPopover for selection/lasso toggle as well
          if (
            (value === "selection" || value === "lasso") &&
            isCompactStylesPanel
          ) {
            return (
              <React.Fragment key={value}>
                <ToolPopover
                  key={"selection-popover"}
                  app={app}
                  options={SELECTION_TOOLS}
                  activeTool={activeTool}
                  defaultOption={app.state.preferredSelectionTool.type}
                  namePrefix="selectionType"
                  title={capitalizeString(t("toolBar.selection"))}
                  data-testid="toolbar-selection"
                  onToolChange={(type: string) => {
                    if (type === "selection" || type === "lasso") {
                      app.setActiveTool({ type });
                      setAppState({
                        preferredSelectionTool: { type, initialized: true },
                      });
                    }
                  }}
                  displayedOption={
                    SELECTION_TOOLS.find(
                      (tool) =>
                        tool.type === app.state.preferredSelectionTool.type,
                    ) || SELECTION_TOOLS[0]
                  }
                  fillable={activeTool.type === "selection"}
                />
                {canvasPopoverJSX}
              </React.Fragment>
            );
          }

          if (value === "triangle" || value === "ellipse") {
            return null;
          }

          if (value === "rectangle") {
            const displayedShape =
              GEOMETRIC_SHAPES.find(
                (tool) => tool.type === app.state.preferredGeometricShape.type,
              ) || GEOMETRIC_SHAPES[0];

            return (
              <ToolPopover
                key={"geometric-shapes-popover"}
                app={app}
                options={GEOMETRIC_SHAPES}
                activeTool={activeTool}
                defaultOption={app.state.preferredGeometricShape.type}
                namePrefix="geometricShapeType"
                title={
                  activeTool.type === "rectangle" ||
                  activeTool.type === "triangle" ||
                  activeTool.type === "ellipse"
                    ? displayedShape.title
                    : capitalizeString(t("headings.shapes"))
                }
                data-testid="toolbar-geometric-shapes"
                onToolChange={(type: string) => {
                  if (
                    type === "rectangle" ||
                    type === "triangle" ||
                    type === "ellipse"
                  ) {
                    app.setActiveTool({ type });
                    setAppState({
                      preferredGeometricShape: {
                        type: type as "rectangle" | "triangle" | "ellipse",
                        initialized: true,
                      },
                    });
                  }
                }}
                displayedOption={{
                  ...displayedShape,
                  icon:
                    activeTool.type === "rectangle" ||
                    activeTool.type === "triangle" ||
                    activeTool.type === "ellipse"
                      ? displayedShape.icon
                      : GeometricShapesIcon,
                }}
                fillable={true}
                selectOnOpen={false}
                side="right"
                align="center"
              />
            );
          }

          if (value === "freedraw") {
            const displayedFreedraw =
              FREEHAND_TOOLS.find(
                (tool) => tool.type === app.state.preferredFreedrawTool.type,
              ) || FREEHAND_TOOLS[0];

            return (
              <ToolPopover
                key={"freedraw-tools-popover"}
                app={app}
                options={FREEHAND_TOOLS}
                activeTool={activeTool}
                defaultOption={app.state.preferredFreedrawTool.type}
                namePrefix="freedrawToolType"
                title={
                  activeTool.type === "freedraw" || activeTool.type === "laser"
                    ? displayedFreedraw.title
                    : capitalizeString(t("toolBar.freedraw"))
                }
                data-testid="toolbar-freedraw-tools"
                onToolChange={(type: string) => {
                  if (type === "freedraw" || type === "laser") {
                    app.setActiveTool({ type });
                    setAppState({
                      preferredFreedrawTool: {
                        type: type as "freedraw" | "laser",
                        initialized: true,
                      },
                    });
                  }
                }}
                displayedOption={{
                  ...displayedFreedraw,
                  icon:
                    activeTool.type === "freedraw" ||
                    activeTool.type === "laser"
                      ? displayedFreedraw.icon
                      : FreedrawIcon,
                }}
                fillable={false}
                selectOnOpen={false}
                side="right"
                align="center"
              />
            );
          }

          if (value === "line") {
            return null;
          }

          if (value === "arrow") {
            const displayedLineTool =
              LINE_TOOLS.find(
                (tool) => tool.type === app.state.preferredLineTool.type,
              ) || LINE_TOOLS[0];

            return (
              <ToolPopover
                key={"line-tools-popover"}
                app={app}
                options={LINE_TOOLS}
                activeTool={activeTool}
                defaultOption={app.state.preferredLineTool.type}
                namePrefix="lineToolType"
                title={
                  activeTool.type === "arrow" || activeTool.type === "line"
                    ? displayedLineTool.title
                    : capitalizeString(t("toolBar.arrow"))
                }
                data-testid="toolbar-line-tools"
                onToolChange={(type: string) => {
                  if (type === "arrow" || type === "line") {
                    app.setActiveTool({ type });
                    setAppState({
                      preferredLineTool: {
                        type: type as "arrow" | "line",
                        initialized: true,
                      },
                    });
                  }
                }}
                displayedOption={{
                  ...displayedLineTool,
                  icon:
                    activeTool.type === "arrow" || activeTool.type === "line"
                      ? displayedLineTool.icon
                      : ArrowIcon,
                }}
                fillable={true}
                selectOnOpen={false}
                side="right"
                align="center"
              />
            );
          }

          const mainButton = (
            <ToolButton
              className={clsx("Shape", { fillable })}
              key={value}
              type="radio"
              icon={icon}
              checked={activeTool.type === value}
              name={radioGroupName}
              title={`${capitalizeString(label)} — ${shortcut}`}
              keyBindingLabel={keybindingLabel}
              aria-label={capitalizeString(label)}
              aria-keyshortcuts={shortcut}
              data-testid={`toolbar-${value}`}
              onPointerDown={({ pointerType }) => {
                if (!app.state.penDetected && pointerType === "pen") {
                  app.togglePenMode(true);
                }

                if (value === "selection") {
                  if (app.state.activeTool.type === "selection") {
                    app.setActiveTool({ type: "lasso" });
                  } else {
                    app.setActiveTool({ type: "selection" });
                  }
                }
              }}
              onChange={({ pointerType }) => {
                if (app.state.activeTool.type !== value) {
                  trackEvent("toolbar", value, "ui");
                }
                if (value === "image") {
                  app.setActiveTool({
                    type: value,
                  });
                } else {
                  app.setActiveTool({ type: value });
                }
              }}
            />
          );

          if (value === "selection" || value === "lasso") {
            return (
              <React.Fragment key={value}>
                {mainButton}
                {canvasPopoverJSX}
              </React.Fragment>
            );
          }

          return mainButton;
        },
      )}
    </>
  );
};

export const ZoomActions = ({
  renderAction,
  zoom,
  onScrollBackToContent,
}: {
  renderAction: ActionManager["renderAction"];
  zoom: Zoom;
  onScrollBackToContent: () => void;
}) => (
  <Stack.Col gap={1} className={CLASSES.ZOOM_ACTIONS}>
    <Stack.Row align="center">
      <ToolButton
        type="button"
        className="adjust-position-button zoom-button"
        title="Ajustar Posição"
        aria-label="Ajustar Posição"
        icon={resizeIcon}
        onClick={onScrollBackToContent}
      />
      {renderAction("zoomOut")}
      {renderAction("resetZoom")}
      {renderAction("zoomIn")}
    </Stack.Row>
  </Stack.Col>
);

export const UndoRedoActions = ({
  renderAction,
  className,
}: {
  renderAction: ActionManager["renderAction"];
  className?: string;
}) => (
  <div className={`undo-redo-buttons ${className}`}>
    <div className="undo-button-container">
      <Tooltip label={t("buttons.undo")}>{renderAction("undo")}</Tooltip>
    </div>
    <div className="redo-button-container">
      <Tooltip label={t("buttons.redo")}> {renderAction("redo")}</Tooltip>
    </div>
  </div>
);

export const ExitZenModeButton = ({
  actionManager,
  showExitZenModeBtn,
}: {
  actionManager: ActionManager;
  showExitZenModeBtn: boolean;
}) => (
  <button
    type="button"
    className={clsx("disable-zen-mode", {
      "disable-zen-mode--visible": showExitZenModeBtn,
    })}
    onClick={() => actionManager.executeAction(actionToggleZenMode)}
  >
    {t("buttons.exitZenMode")}
  </button>
);

export const ExitViewModeButton = ({
  actionManager,
}: {
  actionManager: ActionManager;
}) => (
  <button
    type="button"
    className="disable-view-mode"
    onClick={() => actionManager.executeAction(actionToggleViewMode)}
  >
    {pencilIcon}
  </button>
);
